import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { CreditCard, Shield, Info } from "lucide-react";
import { Alert, AlertDescription } from "components/ui/alert";
import { STRIPE_BILLING_URL, STRIPE_BILLING_TEST_URL } from "utils/constants";
import { isLocalhost, isStaging, isDemo } from "utils/axios-instance";
import { Mixpanel } from "utils/Analytics";
import baseApi from "utils/axios-instance";
import { toast } from "sonner";

interface PaymentStepProps {
  isFinalStep?: boolean;
}

export default function PaymentStep({
  isFinalStep = false,
}: PaymentStepProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async () => {
    setIsSubmitting(true);

    try {
      const response = await baseApi.get("/onboarding_payment_method_status", {
        withCredentials: true,
      });
      if (response.data?.has_payment_method) {
        Mixpanel.track("Onboarding payment submitted");
        navigate("/dashboard", { replace: true });
        return;
      }

      toast.error("Please add a credit card in Stripe before continuing.");
    } catch (error: any) {
      console.error("Payment submission error:", error);
      Mixpanel.track("Error: Payment submission", {
        error: error.message,
      });

      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Failed to complete payment setup. Please try again.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const stripeBillingUrl =
    isLocalhost || isStaging || isDemo
      ? STRIPE_BILLING_TEST_URL
      : STRIPE_BILLING_URL;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-green-50 rounded-xl">
            <CreditCard className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Payment Setup</h2>
        <p className="text-lg text-gray-600">
          Set up your payment method to complete your account
        </p>
      </div>

      <div className="space-y-8">
        {/* Payment Method Card */}
        <Card className="border border-gray-200">
          <CardHeader className="pb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <CreditCard className="h-5 w-5 text-blue-600" />
              </div>
              <CardTitle className="text-xl font-semibold">
                Payment Method
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                Your payment information is secured with enterprise-grade
                encryption. We use Stripe for secure payment processing.
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">
                  Add Payment Method
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Please add a credit card for your team. This will be used
                  for monthly billing.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    window.open(
                      stripeBillingUrl,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                  className="w-full sm:w-auto border-[#005151] text-[#005151] hover:bg-[#005151] hover:text-white"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Add Payment Method via Stripe
                </Button>
                <p className="text-xs text-gray-500 mt-3">
                  Credit card setup is required to complete onboarding.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Information Card */}
        <Card className="bg-gradient-to-r from-[#005151]/5 to-emerald-50 border-[#005151]/20">
          <CardContent className="p-6">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-[#005151] mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Payment Information
                </h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• You will be charged $4 per call with no minimum usage for the 1 month trial. </li>
                  <li>• You can update your payment method at any time</li>
                  <li>• Billing occurs monthly based on your usage</li>
                  <li>• Cancel or change your plan anytime</li>
                  <li>• You won't be charged until you make a new request</li>
                  <li>• Get in touch with us at support@healthharbor.co if you have any questions</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button
            type="button"
            className="bg-[#005151] hover:bg-[#004040] text-white px-8"
            disabled={isSubmitting}
            onClick={onSubmit}
          >
            {isSubmitting
              ? "Completing Setup..."
              : isFinalStep
                ? "Complete Setup"
                : "Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
